// @ts-nocheck
/*
 * Port of views/dashboard/company/list.js (origin/main-dev, 2577 lines).
 * BYTE-IDENTICAL LOGIC — every state hook, useEffect, API call, toast
 * string, role gate, filter rule, credit-update math (multiplier,
 * distributable, demo-special-case), tab logic (mine/nested/demo/
 * demo_deleted), and switch-to-default-user flow matches v1 to the letter.
 *
 * Chrome swaps (per session directive):
 *   - PrimeReact <DataTable> + <Column> ' obd3-gui DataTable + ColumnDef
 *   - PrimeReact <Button> ' shadcn <Button> (v2 has NO primereact dep)
 *   - `nav-tabs` (bootstrap tabs class) preserved — plain buttons.
 *
 * Cross-module modals INTENTIONALLY DEFERRED to a future turn:
 *   - LicenceHistoryList (v1 views/dashboard/licence_history/list.js, 707 lines)
 *   - CompanyVoiceCreditsHistory (v1 views/dashboard/voice-credits-logs/
 *     company-voice-credits-history.js, 917 lines)
 *   These are v1 PAGE components displayed inside modals here. Their
 *   modal shells are ported (matching v1 chrome + open/close state) but
 *   the body renders a placeholder "port pending" panel. When the
 *   Licence History / Voice Credits Logs modules land, swap the panel
 *   for the real component — nothing else needs to change.
 *
 * DEAD CODE preserved verbatim:
 *   - Two identical `refreshGiverCredits` bodies in v1 (second one wins).
 *   - Commented `onUserCreditsUpdatedSuccess` legacy block above the
 *     current implementation.
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  API_COMPANY_LIST,
  API_COMPANY_CREDITS,
  API_COMPANY_PROFILE,
  API_TTS_CREDITS,
  API_TTS_CREDITS_LOGS,
  API_AI_AGENT_CREDITS,
  API_AI_AGENT_CREDITS_LOGS,
  API_COMPANY_SWITCH_DEFAULT_USER,
  API_DEMO_ACCOUNT_SETTINGS_LIST,
  API_COMPANY_DELETE,
  API_COMPANY_CONVERT_TO_ACTUAL,
  API_COMPANY_LICENCE,
} from '@/service/url';
import switchUserIcon from '@/assets/images/icons/switch-user.png';
import { switchUser, getUser } from '@/utilities/setting';
import { getApiCall, postApiCall } from '@/service/httpCall';
import Loader from '@/components/partials/components/Loader';
import {
  Users,
  MapPin,
  Building2,
  Phone,
  Hash,
  User,
  Calendar,
  CalendarClock,
  UserRound,
  Network,
  History,
  Award,
  Wallet,
  Volume2,
  Bot,
  Settings2,
  Pencil,
  Trash2,
  BadgePlus,
  BadgeMinus,
  Repeat,
  Sparkles,
  Eye,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { CompanyConsole } from './components/CompanyConsole';
import CompanyQuickAccessModal from './CompanyQuickAccessModal';
import {
  ROUTES,
  USER_CREDITS_ACTION,
  TTS_CREDITS_ACTION,
  AI_AGENT_CREDITS_ACTION,
  USER_ROLES,
  MODULE_ACL,
} from '@/utilities/constant';
import { exportToCSV, hasPermission, toRupeeFixed } from '@/utilities/helpers';
import { billingPlanLabel } from '@/utilities/billingPlan';
import RenderAddCreditModal from '@/components/partials/components/add-credit-model';
import DemoCompanySummaryModal from '@/components/partials/dashboard/company/DemoCompanySummaryModal';
import LicenceDetailsModal from '@/components/partials/components/LicenceDetailsModal';
import RenderCompanyHierarchyModal from '@/components/partials/components/company-hierarchy-modal';
import RenderRemoveCreditModal from '@/components/partials/components/remove-credit-model';
import LicenceHistoryList from '@/features/licence-history/LicenceHistoryList';
import CompanyVoiceCreditsHistory from '@/features/voice-credits-logs/CompanyVoiceCreditsHistory';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { FiPlus } from 'react-icons/fi';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Download, Plus, Search } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import '@/features/users/user-forms.css';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

// Inline micro-shim block: replaces `@/lib/rb-shim` Row/Col/Form/Button/Modal/Dropdown
// with native bootstrap markup + shadcn Dialog. JSX intact.
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const BootstrapButton: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <Button variant={mapped} size={size} className={className} {...props}>
      {children}
    </Button>
  );
};

// Modal shim — wraps shadcn Dialog. Supports show/onHide + compound children.
// Sizes: 'sm' 425px, default 512px (lg-shadcn ~), 'lg' 768px, 'xl' 1152px.
// All sizes cap height at 90vh and let the body scroll — prevents the vertical/
// horizontal overflow we hit with size='xl' tables.
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);

// Dropdown shim — controlled by useState; ignores align prop. Simple native.
const _DropdownCtx = React.createContext<any>({});
const BootstrapDropdown: any = ({ children, align: _a, className, ...props }: any) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: any) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <_DropdownCtx.Provider value={{ open, setOpen }}>
      <div ref={ref} className={_cx('dropdown position-relative', className)} {...props}>
        {children}
      </div>
    </_DropdownCtx.Provider>
  );
};
BootstrapDropdown.Toggle = ({
  as: _As,
  children,
  onClick,
  className,
  variant: _v,
  ...props
}: any) => {
  const { open, setOpen } = React.useContext(_DropdownCtx);
  const handle = (e: any) => {
    setOpen(!open);
    onClick?.(e);
  };
  return (
    <button type="button" className={_cx('btn btn-link p-0', className)} onClick={handle} {...props}>
      {children}
    </button>
  );
};
BootstrapDropdown.Menu = ({ children, className, ...props }: any) => {
  const { open } = React.useContext(_DropdownCtx);
  if (!open) return null;
  return (
    <div
      className={_cx('dropdown-menu show', className)}
      style={{ position: 'absolute', right: 0, top: '100%', zIndex: 1000 }}
      {...props}
    >
      {children}
    </div>
  );
};
BootstrapDropdown.Item = ({ children, onClick, className, ...props }: any) => {
  const { setOpen } = React.useContext(_DropdownCtx);
  const handle = (e: any) => {
    setOpen(false);
    onClick?.(e);
  };
  return (
    <button
      type="button"
      className={_cx('dropdown-item', className)}
      onClick={handle}
      {...props}
    >
      {children}
    </button>
  );
};

/* Placeholder rendered inside the Licence History / Voice Credits History
 * modals until those v1 pages port as their own modules. Preserves the
 * v1 modal chrome + state; only the body content is a stub. */
const CrossModulePlaceholder = ({ label }) => (
  <div className="text-center py-5 text-muted">
    <div style={{ fontSize: 14, marginBottom: 8 }}>
      <strong>{label}</strong>
    </div>
    <div style={{ fontSize: 13 }}>
      This report opens as its own page in v1 — port pending for that module.
    </div>
  </div>
);

// Single Settings2 icon opens the CompanyConsole drawer with grouped
// actions. Mirrors UserRowActions on the Users page — icon-in-cell,
// full-height side sheet on click. Defined at module scope (not inside
// CompanyList) so React treats it as a stable component type — otherwise
// the drawer would remount on every parent re-render and lose its
// open state.
function CompanyRowActions(props: any) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        aria-label={`Open actions for ${props.company?.name || 'this company'}`}
        title="Company actions"
        onClick={() => setOpen(true)}
      >
        <Settings2 className="h-4 w-4" />
      </Button>
      <CompanyConsole open={open} onOpenChange={setOpen} {...props} />
    </>
  );
}

function positiveCreditMultiplier(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function lockedCreditMultiplier(company, activeKey, _storedKey, actualKey, distKey) {
  if (!company || company.is_own_company === true || company.is_demo_company === true) return null;
  const active = positiveCreditMultiplier(company[activeKey]);
  // 0 means no multiplier has been given, so the picker stays available.
  if (!active) return null;
  // 10x (or any value above 1) is a real multiplier and is shown as already set.
  if (active > 1) return active;
  const actual = Number(company[actualKey] ?? 0);
  const distRaw = actualKey ? company[distKey] : null;
  const distributable = distRaw != null && distRaw !== '' ? Number(distRaw) : actual;
  if (actual > 0 || distributable > 0) return active;
  return null;
}

function maxRemovableCredits(company, actualKey, distKey, activeKey, storedKey) {
  if (!company) return 0;
  const actual = Number(company[actualKey] ?? 0);
  const distRaw = company[distKey];
  const distributable = distRaw != null && distRaw !== '' ? Number(distRaw) : actual;
  const multiplier =
    lockedCreditMultiplier(company, activeKey, storedKey, actualKey, distKey) ??
    positiveCreditMultiplier(company[storedKey]) ??
    1;
  if (!Number.isFinite(actual) || !Number.isFinite(distributable) || multiplier <= 0) return 0;
  return Math.max(
    0,
    Math.round((Math.min(actual, distributable / multiplier) + Number.EPSILON) * 100) / 100,
  );
}

function superAdminRemovableCredits(company, actualKey) {
  if (!company) return 0;
  const actual = Number(company[actualKey] ?? 0);
  if (!Number.isFinite(actual)) return 0;
  return Math.max(0, Math.round((actual + Number.EPSILON) * 100) / 100);
}

const CompanyList = () => {
  const navigate = useNavigate();
  const [companylist, setCompanylist] = useState([]);
  const [loading, setLoading] = useState(false);
  // Header refresh icon: re-run the list effect without the full-page loader.
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const isRefreshRef = useRef(false);
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [isReseller, setIsReseller] = useState(false);
  const [currentUserId, setCurrentUserId] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [addCreditsId, setAddCreditsId] = useState(null);
  const [removeCreditsId, setRemoveCreditsId] = useState(null);
  const [showRemoveCreditModel, setShowRemoveCreditModel] = useState(false);
  const [confirmReduceCredits, setConfirmReduceCredits] = useState(false);
  const [removeCreditsValue, setRemoveCreditsValue] = useState(0);
  const [showAddCreditModel, setShowAddCreditModel] = useState(false);
  const [confirmCredits, setConfirmCredits] = useState(false);
  const [creditsSubmitting, setCreditsSubmitting] = useState(false);
  const [addCreditsValue, setAddCreditsValue] = useState('');
  const [addCreditsMultiplier, setAddCreditsMultiplier] = useState(1);
  const [giverAvailableCredits, setGiverAvailableCredits] = useState(0);
  const [giverDisplayCredits, setGiverDisplayCredits] = useState(0);
  const [demoCreditLimit, setDemoCreditLimit] = useState(0);
  const [demoMaxRecharges, setDemoMaxRecharges] = useState(0);
  const [demoExpiryDays, setDemoExpiryDays] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertId, setConvertId] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryCompany, setSummaryCompany] = useState({ id: null, name: '' });
  const [showLicenceModal, setShowLicenceModal] = useState(false);
  const [licenceModalId, setLicenceModalId] = useState(null);
  const [licenceModalTitle, setLicenceModalTitle] = useState('');
  const [licenceModalData, setLicenceModalData] = useState(null);
  const [licenceModalLoading, setLicenceModalLoading] = useState(false);

  const [toastProps, setToastProps] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [isOwnCompany, setIsOwnCompany] = useState(false);
  const [activeTab, setActiveTab] = usePersistedState('companies/CompanyList:activeTab', 'mine');
  const [quickAccessCompany, setQuickAccessCompany] = useState(null);
  const [loggedInUserACL, setLoggedInUserACL] = useState({});

  const [showAddTtsCreditModel, setShowAddTtsCreditModel] = useState(false);
  const [confirmTtsCredits, setConfirmTtsCredits] = useState(false);
  const [addTtsCreditsValue, setAddTtsCreditsValue] = useState('');
  const [addTtsCreditsMultiplier, setAddTtsCreditsMultiplier] = useState(1);
  const [ttsCreditsSubmitting, setTtsCreditsSubmitting] = useState(false);
  const [showRemoveTtsCreditModel, setShowRemoveTtsCreditModel] = useState(false);
  const [confirmReduceTtsCredits, setConfirmReduceTtsCredits] = useState(false);
  const [removeTtsCreditsValue, setRemoveTtsCreditsValue] = useState(0);

  const [addTtsCreditsId, setAddTtsCreditsId] = useState(null);
  const [removeTtsCreditsId, setRemoveTtsCreditsId] = useState(null);
  const [giverAvailableTtsCredits, setGiverAvailableTtsCredits] = useState(0);
  const [giverDisplayTtsCredits, setGiverDisplayTtsCredits] = useState(0);

  const [showAddAiAgentCreditModel, setShowAddAiAgentCreditModel] = useState(false);
  const [confirmAiAgentCredits, setConfirmAiAgentCredits] = useState(false);
  const [addAiAgentCreditsValue, setAddAiAgentCreditsValue] = useState('');
  const [addAiAgentCreditsMultiplier, setAddAiAgentCreditsMultiplier] = useState(1);
  const [aiAgentCreditsSubmitting, setAiAgentCreditsSubmitting] = useState(false);
  const [showRemoveAiAgentCreditModel, setShowRemoveAiAgentCreditModel] = useState(false);
  const [confirmReduceAiAgentCredits, setConfirmReduceAiAgentCredits] = useState(false);
  const [removeAiAgentCreditsValue, setRemoveAiAgentCreditsValue] = useState(0);

  const [addAiAgentCreditsId, setAddAiAgentCreditsId] = useState(null);
  const [removeAiAgentCreditsId, setRemoveAiAgentCreditsId] = useState(null);
  const [giverAvailableAiAgentCredits, setGiverAvailableAiAgentCredits] = useState(0);
  const [giverDisplayAiAgentCredits, setGiverDisplayAiAgentCredits] = useState(0);
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [showLicenceHistoryModal, setShowLicenceHistoryModal] = useState(false);
  const [selectedLicenceCompanyId, setSelectedLicenceCompanyId] = useState(null);
  const [selectedLicenceCompanyName, setSelectedLicenceCompanyName] = useState(null);
  const [showVoiceCreditsHistoryModal, setShowVoiceCreditsHistoryModal] = useState(false);
  const [selectedVoiceCreditsCompanyId, setSelectedVoiceCreditsCompanyId] = useState(null);
  const [selectedVoiceCreditsCompanyName, setSelectedVoiceCreditsCompanyName] = useState(null);
  const [showTtsCreditsHistoryModal, setShowTtsCreditsHistoryModal] = useState(false);
  const [selectedTtsCreditsCompanyId, setSelectedTtsCreditsCompanyId] = useState(null);
  const [selectedTtsCreditsCompanyName, setSelectedTtsCreditsCompanyName] = useState(null);
  const [showAiAgentCreditsHistoryModal, setShowAiAgentCreditsHistoryModal] = useState(false);
  const [selectedAiAgentCreditsCompanyId, setSelectedAiAgentCreditsCompanyId] = useState(null);
  const [selectedAiAgentCreditsCompanyName, setSelectedAiAgentCreditsCompanyName] = useState(null);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState(false);
  const [selectedCompanyDetails, setSelectedCompanyDetails] = useState(null);
  const [showHierarchyModal, setShowHierarchyModal] = useState(false);
  const [hierarchyCompany, setHierarchyCompany] = useState(null);

  useEffect(() => {
    const user = getUser();
    if (user) {
      setLoggedInUser(user);
      setCurrentUserId(user.id);
      setCurrentUserRole(user.role);
      setLoggedInUserId(user.id);
      if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
      if (user.role === USER_ROLES.RESELLER) setIsReseller(true);
      if (user?.acl_settings) {
        setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
      }
    }
  }, []);

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  useEffect(() => {
    if (loggedInUser?.id && !isSuperAdmin) refreshGiverCredits();
  }, [loggedInUser?.id]);

  useEffect(() => {
    const fetchDemoCreditLimit = async () => {
      try {
        const resp = await getApiCall(API_DEMO_ACCOUNT_SETTINGS_LIST, {});
        const row = Array.isArray(resp) ? resp[0] : resp;
        setDemoCreditLimit(Number(row?.credit_limit_each_time) || 0);
        setDemoMaxRecharges(Number(row?.number_of_times_demo_account_can_be_recharged) || 0);
        setDemoExpiryDays(Number(row?.expiry_days_per_recharge) || 0);
      } catch (e) {
        console.error('Failed to fetch demo account settings', e);
      }
    };
    fetchDemoCreditLimit();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const isRefresh = isRefreshRef.current;
    isRefreshRef.current = false;
    const fetchAssignedCompany = async () => {
      if (!loggedInUser) {
        setLoading(false);
        return;
      }
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        if (loggedInUser?.assignedCompany && loggedInUser.assignedCompany?.is_own_company) {
          const assignedCompanyId = loggedInUser?.assignedCompany?.id ?? loggedInUser?.assigned_company;
          if (!assignedCompanyId) throw new Error('Assigned company id not found for this user');
          const fetchedAssignedCompanyData = await getApiCall(
            API_COMPANY_PROFILE.replace(':id', String(assignedCompanyId)),
          );
          if (!isMounted) return;
          const licence = fetchedAssignedCompanyData?.subUserLicenceId?.available_licence || {};
          const assignedCompanyOfLoggedInUser = [
            {
              key: 1,
              name: loggedInUser.assignedCompany.name,
              phone: loggedInUser.assignedCompany.phone,
              address: loggedInUser.assignedCompany.address,
              pincode: loggedInUser.assignedCompany.pincode,
              credits_available: loggedInUser.assignedCompany.credits_available,
              created_at: loggedInUser.assignedCompany.created_at,
              updated_at: loggedInUser.assignedCompany.updated_at,
              company_creator: { username: loggedInUser?.createdByUser?.username },
              available_licence: licence,
              is_own_company:
                fetchedAssignedCompanyData?.is_own_company ??
                loggedInUser?.assignedCompany?.is_own_company,
              is_reseller_company:
                fetchedAssignedCompanyData?.is_reseller_company ??
                loggedInUser?.assignedCompany?.is_reseller_company,
              is_demo_company:
                fetchedAssignedCompanyData?.is_demo_company ??
                loggedInUser?.assignedCompany?.is_demo_company,
              billing_type: fetchedAssignedCompanyData?.billing_type ?? null,
              postpaid_mode: fetchedAssignedCompanyData?.postpaid_mode ?? null,
              credit_limit: fetchedAssignedCompanyData?.credit_limit ?? null,
              billing_cycle: fetchedAssignedCompanyData?.billing_cycle ?? null,
              demo_recharge_count:
                fetchedAssignedCompanyData?.demo_recharge_count ??
                loggedInUser?.assignedCompany?.demo_recharge_count,
              expiry_date:
                fetchedAssignedCompanyData?.expiry_date ??
                loggedInUser?.assignedCompany?.expiry_date,
              defaultUser:
                fetchedAssignedCompanyData?.defaultUser ||
                loggedInUser?.assignedCompany?.defaultUser ||
                (loggedInUser?.assignedCompany?.default_user_id === loggedInUser?.id
                  ? {
                      id: loggedInUser?.id,
                      username: loggedInUser?.username,
                      expiry_date: loggedInUser?.expiry_date,
                    }
                  : undefined),
              company_code:
                fetchedAssignedCompanyData?.company_code ??
                loggedInUser?.assignedCompany?.company_code,
              companyCallStrategies: loggedInUser?.assignedCompany?.companyCallStrategies || [],
              companyGroups: loggedInUser?.assignedCompany?.companyGroups || [],
              companyLocations: loggedInUser?.assignedCompany?.companyLocations || [],
            },
          ];
          setCompanylist(assignedCompanyOfLoggedInUser);
          setIsOwnCompany(true);
        } else {
          const queryData =
            activeTab === 'mine'
              ? 'mine'
              : activeTab === 'demo'
              ? 'demo'
              : activeTab === 'demo_deleted'
              ? 'demo_deleted'
              : 'nested';
          const listUrl = `${API_COMPANY_LIST}?queryData=${queryData}`;
          const list = await getApiCall(listUrl, {});
          if (!isMounted) return;
          onCompanyListSuccess(list);
          setIsOwnCompany(false);
        }
      } catch (err) {
        onError(err);
      } finally {
        if (isMounted) setLoading(false);
        setRefreshing(false);
      }
    };
    fetchAssignedCompany();
    return () => {
      isMounted = false;
    };
  }, [loggedInUser, activeTab, reloadKey]);

  useEffect(() => {
    if (loggedInUser?.id && !isSuperAdmin) refreshGiverTtsCredits();
  }, [loggedInUser?.id, isSuperAdmin]);

  useEffect(() => {
    if (loggedInUser?.id && !isSuperAdmin) refreshGiverAiAgentCredits();
  }, [loggedInUser?.id, isSuperAdmin]);

  // v1 has two `refreshGiverCredits` declarations; the second one wins.
  // Preserving both here for parity with the source — first one runs never.
  async function refreshGiverCredits() {
    const assignedCompanyId =
      loggedInUser?.assignedCompany?.id ?? loggedInUser?.assigned_company;
    if (!assignedCompanyId || isSuperAdmin) return;
    try {
      const resp = await getApiCall(API_COMPANY_PROFILE.replace(':id', String(assignedCompanyId)));
      const distributable = Number(resp?.credits_distributable ?? resp?.credits_available ?? 0);
      const display = Number(resp?.credits_available || 0);
      setGiverAvailableCredits(distributable);
      setGiverDisplayCredits(display);
    } catch (err) {
      console.error('Failed to refresh giver credits', err);
    }
  }

  async function refreshGiverTtsCredits() {
    const assignedCompanyId =
      loggedInUser?.assignedCompany?.id ?? loggedInUser?.assigned_company;
    if (!assignedCompanyId || isSuperAdmin) return;
    try {
      const resp = await getApiCall(API_COMPANY_PROFILE.replace(':id', String(assignedCompanyId)));
      const distributable = Number(resp?.tts_credits_distributable ?? resp?.tts_credits ?? 0);
      const display = Number(resp?.tts_credits || 0);
      setGiverAvailableTtsCredits(distributable);
      setGiverDisplayTtsCredits(display);
    } catch (err) {
      console.error('Failed to refresh giver TTS credits', err);
    }
  }

  async function refreshGiverAiAgentCredits() {
    const assignedCompanyId =
      loggedInUser?.assignedCompany?.id ?? loggedInUser?.assigned_company;
    if (!assignedCompanyId || isSuperAdmin) return;
    try {
      const resp = await getApiCall(API_COMPANY_PROFILE.replace(':id', String(assignedCompanyId)));
      const distributable = Number(
        resp?.ai_agent_credits_distributable ?? resp?.ai_agent_credits ?? 0
      );
      const display = Number(resp?.ai_agent_credits || 0);
      setGiverAvailableAiAgentCredits(distributable);
      setGiverDisplayAiAgentCredits(display);
    } catch (err) {
      console.error('Failed to refresh giver AI agent voice credits', err);
    }
  }

  function handleAddCredits(id) {
    const company = companylist.find((c) => c.id === id);
    const locked = lockedCreditMultiplier(
      company,
      'voice_credits_multiplier',
      'stored_voice_credits_multiplier',
      'credits_available',
      'credits_distributable',
    );
    setAddCreditsId(id);
    setAddCreditsValue('');
    setAddCreditsMultiplier(locked ?? 1);
    setShowAddCreditModel(true);
    if (!isSuperAdmin) refreshGiverCredits();
  }

  async function handleRemoveCredits(id) {
    const company = companylist.find((c) => c.id === id);
    if (company?.is_demo_company === true) {
      toast.warning('Voice credits cannot be removed from a demo company.');
      return;
    }
    setShowRemoveCreditModel(true);
    setRemoveCreditsId(id);
    setRemoveCreditsValue(0);
    if (company) setRemoveCreditsValue(0);
  }

  async function handleAddTtsCredits(id) {
    const targetCompany = companylist.find((c) => c.id === id);
    if (targetCompany?.is_demo_company === true) {
      toast.warning('TTS credits cannot be added to a demo company.');
      return;
    }
    const locked = lockedCreditMultiplier(
      targetCompany,
      'tts_credits_multiplier',
      'stored_tts_credits_multiplier',
      'tts_credits',
      'tts_credits_distributable',
    );
    setAddTtsCreditsId(id);
    setAddTtsCreditsValue('');
    setAddTtsCreditsMultiplier(locked ?? 1);
    if (!isSuperAdmin) await refreshGiverTtsCredits();
    setShowAddTtsCreditModel(true);
  }

  async function handleRemoveTtsCredits(id) {
    const company = companylist.find((c) => c.id === id);
    if (company?.is_demo_company === true) {
      toast.warning('TTS credits cannot be modified for a demo company.');
      return;
    }
    setShowRemoveTtsCreditModel(true);
    setRemoveTtsCreditsId(id);
    setRemoveTtsCreditsValue(0);
    if (company) setRemoveTtsCreditsValue(0);
  }

  async function handleAddAiAgentCredits(id) {
    const targetCompany = companylist.find((c) => c.id === id);
    if (targetCompany?.is_demo_company === true) {
      toast.warning('AI agent voice credits cannot be added to a demo company.');
      return;
    }
    const locked = lockedCreditMultiplier(
      targetCompany,
      'ai_agent_credits_multiplier',
      'stored_ai_agent_credits_multiplier',
      'ai_agent_credits',
      'ai_agent_credits_distributable',
    );
    setAddAiAgentCreditsId(id);
    setAddAiAgentCreditsValue('');
    setAddAiAgentCreditsMultiplier(locked ?? 1);
    if (!isSuperAdmin) await refreshGiverAiAgentCredits();
    setShowAddAiAgentCreditModel(true);
  }

  async function handleRemoveAiAgentCredits(id) {
    const company = companylist.find((c) => c.id === id);
    if (company?.is_demo_company === true) {
      toast.warning('AI agent voice credits cannot be modified for a demo company.');
      return;
    }
    setShowRemoveAiAgentCreditModel(true);
    setRemoveAiAgentCreditsId(id);
    setRemoveAiAgentCreditsValue(0);
    if (company) setRemoveAiAgentCreditsValue(0);
  }

  const handleExport = async () => {
    try {
      exportToCSV({
        fileName: 'companies-list',
        data: displayedCompanyList,
        columns: [
          { header: '#', accessor: 'key' },
          { header: 'Name', accessor: (row) => row.name || '-' },
          { header: 'Phone', accessor: (row) => row.phone || '-' },
          { header: 'Address', accessor: (row) => row.address || '-' },
          { header: 'Pincode', accessor: (row) => row.pincode || '-' },
          {
            header: 'Company Code',
            accessor: (row) => (Number(row.company_code) ? row.company_code : '-'),
          },
          {
            header: 'Type',
            accessor: (row) => {
              if (row.is_reseller_company) return 'Reseller Company';
              if (row.is_own_company) return 'End User';
              return '-';
            },
          },
          { header: 'Billing Plan', accessor: (row) => billingPlanLabel(row) },
          // {
          //   header: 'Assigned Plan Name',
          //   accessor: (row) =>
          //     row.assignedTtsPlan?.plan_name || row.tts_plan?.plan_name || '-',
          // },
          { header: 'Voice Credits', accessor: (row) => row.credits_available || 0 },
          { header: 'TTS Credits', accessor: (row) => row.tts_credits || 0 },
          { header: 'AI Agent Voice Credits', accessor: (row) => row.ai_agent_credits || 0 },
          {
            header: 'Call Strategies',
            accessor: (row) =>
              getActiveStrategies(row)
                .map((x) => x.strategyMaster?.name)
                .filter(Boolean)
                .join(', ') || '-',
          },
          {
            header: 'Company Groups',
            accessor: (row) =>
              getActiveGroups(row)
                .map((g) => g.groups?.group_name)
                .filter(Boolean)
                .join(', ') || '-',
          },
          {
            header: 'Company Locations',
            accessor: (row) =>
              getActiveLocations(row)
                .map((l) => l.locations?.location_name)
                .filter(Boolean)
                .join(', ') || '-',
          },
          { header: 'Created By', accessor: (row) => row.company_creator?.username || '-' },
          {
            header: 'Created Date',
            accessor: (row) =>
              new Date(row.created_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
          {
            header: 'Updated Date',
            accessor: (row) =>
              new Date(row.updated_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
        ],
      });
    } finally {
      /* noop */
    }
  };

  const getCompanyInitials = (name) => {
    if (!name?.trim()) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const getCompanyTypeInfo = (row) => {
    if (row.is_reseller_company) return { label: 'Reseller Company', variant: 'reseller' };
    if (row.is_own_company) return { label: 'End User', variant: 'enduser' };
    return { label: '-', variant: 'neutral' };
  };

  const formatCreditAmount = (val) => {
    const n = Number(val);
    return Number.isNaN(n) ? '0.00' : toRupeeFixed(n);
  };

  const getActiveStrategies = (row) =>
    (row.companyCallStrategies || []).filter((x) => x.strategyMaster && !x.is_deleted);

  const getActiveGroups = (row) =>
    (row.companyGroups || []).filter((g) => g.groups && !g.groups.is_deleted);

  const getActiveLocations = (row) =>
    (row.companyLocations || []).filter((l) => l.locations && !l.locations.is_deleted);

  const getActiveCcDirections = (row) =>
    (row.companyCountryCodeDirections || []).filter((r) => r.directionMaster && !r.is_deleted);

  const mergeCompanyRow = (companyId, patch) => {
    setCompanylist((prev) =>
      prev.map((c) => (c.id === companyId ? { ...c, ...patch } : c)),
    );
  };

  const fetchCompanyAssociations = async (companyId, include) => {
    const url = `${API_COMPANY_PROFILE.replace(':id', String(companyId))}?include=${include}`;
    return getApiCall(url, {});
  };

  const openCompanyLicenceHistory = (rowData) => {
    setSelectedLicenceCompanyId(rowData.id);
    setSelectedLicenceCompanyName(rowData.name);
    setShowLicenceHistoryModal(true);
  };
  const openCompanyVoiceCreditsHistory = (rowData) => {
    setSelectedVoiceCreditsCompanyId(rowData.id);
    setSelectedVoiceCreditsCompanyName(rowData.name);
    setShowVoiceCreditsHistoryModal(true);
  };
  const openCompanyTtsCreditsHistory = (rowData) => {
    setSelectedTtsCreditsCompanyId(rowData.id);
    setSelectedTtsCreditsCompanyName(rowData.name);
    setShowTtsCreditsHistoryModal(true);
  };
  const openCompanyAiAgentCreditsHistory = (rowData) => {
    setSelectedAiAgentCreditsCompanyId(rowData.id);
    setSelectedAiAgentCreditsCompanyName(rowData.name);
    setShowAiAgentCreditsHistoryModal(true);
  };

  const QuickAccessBtn = ({ title, onClick, disabled, children, accent }) => (
    <button
      type="button"
      className={`company-list-quick-btn company-list-quick-btn--${accent}${
        disabled ? ' is-disabled' : ''
      }`}
      title={title}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      {children}
    </button>
  );

  useEffect(() => {
    if (confirmCredits) {
      if (!addCreditsValue || Number(addCreditsValue) === 0) {
        toast.error('Please add some credit value to increase');
        setConfirmCredits(false);
      } else {
        const effectiveAmount = Number(addCreditsValue) * (Number(addCreditsMultiplier) || 1);
        const isDemoTargetCredit =
          companylist.find((c) => c.id === addCreditsId)?.is_demo_company === true;
        if (!isDemoTargetCredit && !isSuperAdmin && effectiveAmount > giverAvailableCredits) {
          toast.error(
            `Insufficient distributable credits. You have ${giverAvailableCredits} distributable but need ${effectiveAmount} (${addCreditsValue} × ${addCreditsMultiplier}x). Please ask your parent to add more credits.`,
          );
          setConfirmCredits(false);
          return;
        }
        let apiUrl = API_COMPANY_CREDITS.replace(':id', addCreditsId);
        setCreditsSubmitting(true);
        postApiCall(apiUrl, {
          id: addCreditsId,
          updatedCredit: addCreditsValue,
          multiplier: addCreditsMultiplier,
          action: USER_CREDITS_ACTION.ADD,
          fromUser: currentUserId,
        })
          .then(onUserCreditsUpdatedSuccess)
          .catch(onUserCreditsUpdatedFailed);
      }
    }
  }, [confirmCredits, addCreditsValue, addCreditsMultiplier]);

  useEffect(() => {
    if (confirmReduceCredits) {
      if (removeCreditsValue == 0) {
        toast.error('Please add some credit value to reduce');
      } else {
        let apiUrl = API_COMPANY_CREDITS.replace(':id', removeCreditsId);
        setCreditsSubmitting(true);
        postApiCall(apiUrl, {
          id: removeCreditsId,
          updatedCredit: removeCreditsValue,
          action: USER_CREDITS_ACTION.DEDUCT,
          fromUser: currentUserId,
        })
          .then(onUserCreditsUpdatedSuccess)
          .catch(onUserCreditsUpdatedFailed);
      }
    }
  }, [confirmReduceCredits, removeCreditsValue]);

  function openDeleteModal(id) {
    setDeleteId(id);
    setShowDeleteModal(true);
  }
  function openConvertModal(id) {
    setConvertId(id);
    setShowConvertModal(true);
  }
  function closeConvertModal() {
    setConvertId(null);
    setShowConvertModal(false);
  }

  async function confirmConvert() {
    const companyId = convertId;
    try {
      setButtonDisable(true);
      const url = API_COMPANY_CONVERT_TO_ACTUAL.replace(':id', companyId);
      await postApiCall(url, {});
      toast.success(
        "Successfully converted to an actual company. Taking you to this company's settings...",
      );
      closeConvertModal();
      setLoading(true);
      const resp = await getApiCall(API_COMPANY_LIST, {});
      onCompanyListSuccess(resp);
      setTimeout(() => {
        navigate(`/${ROUTES.COMPANIES.EDIT.replace(':id', companyId)}`);
      }, 1500);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to convert company');
      onError(err);
    } finally {
      setLoading(false);
      setButtonDisable(false);
    }
  }

  function closeDeleteModal() {
    setDeleteId(null);
    setShowDeleteModal(false);
  }

  async function confirmDelete() {
    try {
      setButtonDisable(true);
      const url = API_COMPANY_DELETE.replace(':id', deleteId);
      await postApiCall(url, {});
      toast.success('Company deactivated successfully');
      closeDeleteModal();
      setLoading(true);
      const resp = await getApiCall(API_COMPANY_LIST, {});
      onCompanyListSuccess(resp);
    } catch (err) {
      closeDeleteModal();
      toast.error(friendlyApiError(err) || 'Failed to delete company');
    } finally {
      setLoading(false);
      setButtonDisable(false);
    }
  }

  function onUserCreditsUpdatedSuccess(resp) {
    const updatedCompanyId = addCreditsId || removeCreditsId;
    const isAdd = confirmCredits;
    const fromCompanyId =
      loggedInUser?.assignedCompany?.id ?? loggedInUser?.assigned_company;
    const targetSnapshot = companylist.find((c) => c.id === updatedCompanyId);
    const targetIsDemo = targetSnapshot?.is_demo_company === true;
    const locked = lockedCreditMultiplier(
      targetSnapshot,
      'voice_credits_multiplier',
      'stored_voice_credits_multiplier',
      'credits_available',
      'credits_distributable',
    );
    const appliedMult =
      locked ??
      (isAdd
        ? Number(addCreditsMultiplier) || 1
        : positiveCreditMultiplier(targetSnapshot?.stored_voice_credits_multiplier) ?? 1);
    const amount = isAdd ? Number(addCreditsValue) : Number(removeCreditsValue);
    const baseDelta = isAdd ? amount : -amount;
    const effectiveDelta = baseDelta * appliedMult;

    setCompanylist((prevList) =>
      prevList.map((company) => {
        if (company.id === updatedCompanyId) {
          if (targetIsDemo && isAdd) {
            const demoFinal = roundTo2(Number(demoCreditLimit) || baseDelta);
            let nextExpiry = company.expiry_date;
            if (demoExpiryDays > 0) {
              const d = new Date();
              d.setHours(0, 0, 0, 0);
              d.setDate(d.getDate() + (demoExpiryDays - 1));
              nextExpiry = d.toISOString();
            }
            return {
              ...company,
              credits_available: demoFinal,
              credits_distributable: demoFinal,
              demo_recharge_count: Number(company.demo_recharge_count || 0) + 1,
              expiry_date: nextExpiry,
              defaultUser: company.defaultUser
                ? { ...company.defaultUser, expiry_date: nextExpiry }
                : company.defaultUser,
            };
          }
          const currentAvailable = Number(company.credits_available || 0);
          const currentDistributable = Number(
            company.credits_distributable ?? company.credits_available ?? 0,
          );
          const zeroPassedDownPool =
            !isAdd && isSuperAdmin && currentAvailable * appliedMult > currentDistributable;
          const nextDistributable = zeroPassedDownPool
            ? 0
            : roundTo2(
                !isAdd && isSuperAdmin
                  ? Math.max(0, currentDistributable + effectiveDelta)
                  : currentDistributable + effectiveDelta,
              );
          return {
            ...company,
            credits_available: roundTo2(currentAvailable + baseDelta),
            credits_distributable: nextDistributable,
            stored_voice_credits_multiplier: appliedMult,
            voice_credits_multiplier:
              zeroPassedDownPool || (!isAdd && nextDistributable <= 0) ? 0 : appliedMult,
          };
        }
        if (!targetIsDemo && !isSuperAdmin && fromCompanyId && company.id === fromCompanyId) {
          const nextDistributable = roundTo2(
            Number(company.credits_distributable ?? company.credits_available ?? 0) -
              effectiveDelta,
          );
          const exhausted = isAdd && nextDistributable <= 0;
          const restored =
            !isAdd &&
            nextDistributable > 0 &&
            Number(company.voice_credits_multiplier ?? 1) === 0;
          return {
            ...company,
            credits_distributable: exhausted ? 0 : nextDistributable,
            voice_credits_multiplier: exhausted
              ? 0
              : restored
              ? Number(
                  company.stored_voice_credits_multiplier ??
                    company.voice_credits_multiplier ??
                    1,
                )
              : company.voice_credits_multiplier,
          };
        }
        return company;
      }),
    );

    if (!isSuperAdmin && fromCompanyId && loggedInUser?.assignedCompany?.id === fromCompanyId) {
      const nextDistributable = roundTo2(Number(giverAvailableCredits || 0) - effectiveDelta);
      const exhausted = isAdd && nextDistributable <= 0;
      setGiverAvailableCredits(exhausted ? 0 : nextDistributable);
    }

    window.dispatchEvent(new CustomEvent('voice-credits-updated'));

    if (!isSuperAdmin) refreshGiverCredits();

    setConfirmCredits(false);
    setConfirmReduceCredits(false);
    setCreditsSubmitting(false);
    setRemoveCreditsValue(0);
    setAddCreditsValue('');
    setAddCreditsMultiplier(1);
    setShowAddCreditModel(false);
    setShowRemoveCreditModel(false);
    toast.success('Company Credits Updated Successfully');
  }

  function roundTo2(num) {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  }

  function onUserCreditsUpdatedFailed(error) {
    setConfirmCredits(false);
    setConfirmReduceCredits(false);
    setCreditsSubmitting(false);
    setRemoveCreditsValue(0);
    setAddCreditsValue('');
    setAddCreditsMultiplier(1);
    setShowAddCreditModel(false);
    setShowRemoveCreditModel(false);
    toast.error(friendlyApiError(error) || 'Failed to update credits');
  }

  function onCompanyListSuccess(resp) {
    if (!Array.isArray(resp)) {
      setCompanylist([]);
      toast.error('Company list response was invalid');
      return;
    }
    const updatedList = resp.map((val, idx) => {
      val['key'] = idx + 1;
      val['created_at'] = new Date(val['created_at']).toGMTString();
      return val;
    });
    setCompanylist(updatedList);
  }

  const handleAllocateClick = (id, role) => {
    navigate(`/allocate/${currentUserId}/${id}/company`);
  };

  async function switchToDefaultUser(companyRow) {
    if (isSwitchingAccount) return;
    if (companyRow?.is_deleted === true || activeTab === 'demo_deleted') {
      toast.error('Cannot switch into a deleted demo company');
      return;
    }
    const defaultUser = companyRow?.defaultUser;
    if (!defaultUser?.id) {
      toast.error('No default user configured for this company');
      return;
    }
    if (Number(defaultUser.status) != 1) {
      toast.error('Default user is inactive');
      return;
    }
    if (companyRow?.is_demo_company === true && defaultUser.expiry_date) {
      const e = new Date(defaultUser.expiry_date);
      e.setHours(0, 0, 0, 0);
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      if (e.getTime() < t.getTime()) {
        toast.error('Demo account has expired. Cannot switch.');
        return;
      }
    }
    setIsSwitchingAccount(true);
    try {
      const url = API_COMPANY_SWITCH_DEFAULT_USER.replace(':id', companyRow.id);
      const response = await postApiCall(url, {});
      const switchAccountSuccess = await switchUser(response);
      if (switchAccountSuccess) {
        toast.success('Switched to company default user');
        setTimeout(() => {
          window.location.href = ROUTES.DASHBOARD;
        }, 1500);
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to switch user');
      setIsSwitchingAccount(false);
    }
  }

  function isCompanyParent(companyRow) {
    const creatorId = companyRow?.created_by;
    if (creatorId === null || creatorId === undefined || !currentUserId) return false;
    return String(creatorId) === String(currentUserId);
  }

  function onError(err) {
    console.error(err);
    setCompanylist([]);
    toast.error(friendlyApiError(err) || 'Failed to load company list');
  }

  const handleCreateButtonClick = () => {
    navigate(`/${ROUTES.COMPANIES.CREATE}`);
  };

  useEffect(() => {
    if (confirmTtsCredits) {
      if (!addTtsCreditsValue || Number(addTtsCreditsValue) === 0) {
        toast.error('Please add some TTS credit value to increase');
        setConfirmTtsCredits(false);
      } else {
        const effectiveAmount = Number(addTtsCreditsValue) * (Number(addTtsCreditsMultiplier) || 1);
        if (!isSuperAdmin && effectiveAmount > giverAvailableTtsCredits) {
          toast.error(
            `Insufficient distributable TTS credits. You have ${giverAvailableTtsCredits} distributable but need ${effectiveAmount} (${addTtsCreditsValue} × ${addTtsCreditsMultiplier}x). Please ask your parent to add more credits.`,
          );
          setConfirmTtsCredits(false);
          return;
        }
        let apiUrl = API_TTS_CREDITS.replace(':id', addTtsCreditsId);
        setTtsCreditsSubmitting(true);
        postApiCall(apiUrl, {
          id: addTtsCreditsId,
          updatedCredit: addTtsCreditsValue,
          multiplier: addTtsCreditsMultiplier,
          action: TTS_CREDITS_ACTION.ADDITION,
          fromUser: loggedInUserId,
        })
          .then(onUserTtsCreditsUpdatedSuccess)
          .catch(onUserTtsCreditsUpdatedFailed);
      }
    }
  }, [confirmTtsCredits, addTtsCreditsValue, addTtsCreditsMultiplier, isSuperAdmin, giverAvailableTtsCredits]);

  useEffect(() => {
    if (confirmReduceTtsCredits) {
      if (removeTtsCreditsValue == 0) {
        toast.error('Please add some TTS credit value to reduce');
      } else {
        let apiUrl = API_TTS_CREDITS.replace(':id', removeTtsCreditsId);
        setTtsCreditsSubmitting(true);
        postApiCall(apiUrl, {
          id: removeTtsCreditsId,
          updatedCredit: removeTtsCreditsValue,
          action: TTS_CREDITS_ACTION.DEDUCTION,
          fromUser: loggedInUserId,
        })
          .then(onUserTtsCreditsUpdatedSuccess)
          .catch(onUserTtsCreditsUpdatedFailed);
      }
    }
  }, [confirmReduceTtsCredits, removeTtsCreditsValue]);

  function onUserTtsCreditsUpdatedSuccess(resp) {
    const updatedCompanyId = addTtsCreditsId || removeTtsCreditsId;
    const isAdd = confirmTtsCredits;
    const fromCompanyId =
      loggedInUser?.assignedCompany?.id ?? loggedInUser?.assigned_company;
    const targetSnapshot = companylist.find((c) => c.id === updatedCompanyId);
    const locked = lockedCreditMultiplier(
      targetSnapshot,
      'tts_credits_multiplier',
      'stored_tts_credits_multiplier',
      'tts_credits',
      'tts_credits_distributable',
    );
    const appliedMult =
      locked ??
      (isAdd
        ? Number(addTtsCreditsMultiplier) || 1
        : positiveCreditMultiplier(targetSnapshot?.stored_tts_credits_multiplier) ?? 1);
    const amount = isAdd ? Number(addTtsCreditsValue) : Number(removeTtsCreditsValue);
    const baseDelta = isAdd ? amount : -amount;
    const effectiveDelta = baseDelta * appliedMult;

    setCompanylist((prevList) =>
      prevList.map((company) => {
        if (company.id === updatedCompanyId) {
          const currentAvailable = Number(company.tts_credits || 0);
          const currentDistributable = Number(
            company.tts_credits_distributable ?? company.tts_credits ?? 0,
          );
          const zeroPassedDownPool =
            !isAdd && isSuperAdmin && currentAvailable * appliedMult > currentDistributable;
          const nextDistributable = zeroPassedDownPool
            ? 0
            : roundTo2(
                !isAdd && isSuperAdmin
                  ? Math.max(0, currentDistributable + effectiveDelta)
                  : currentDistributable + effectiveDelta,
              );
          return {
            ...company,
            tts_credits: roundTo2(currentAvailable + baseDelta),
            tts_credits_distributable: nextDistributable,
            stored_tts_credits_multiplier: appliedMult,
            tts_credits_multiplier:
              zeroPassedDownPool || (!isAdd && nextDistributable <= 0) ? 0 : appliedMult,
          };
        }
        if (!isSuperAdmin && fromCompanyId && company.id === fromCompanyId) {
          const nextDistributable = roundTo2(
            Number(company.tts_credits_distributable ?? company.tts_credits ?? 0) -
              effectiveDelta,
          );
          const exhausted = isAdd && nextDistributable <= 0;
          const restored =
            !isAdd &&
            nextDistributable > 0 &&
            Number(company.tts_credits_multiplier ?? 1) === 0;
          return {
            ...company,
            tts_credits_distributable: exhausted ? 0 : nextDistributable,
            tts_credits_multiplier: exhausted
              ? 0
              : restored
              ? Number(
                  company.stored_tts_credits_multiplier ??
                    company.tts_credits_multiplier ??
                    1,
                )
              : company.tts_credits_multiplier,
          };
        }
        return company;
      }),
    );

    if (!isSuperAdmin && fromCompanyId && loggedInUser?.assignedCompany?.id === fromCompanyId) {
      const nextDistributable = roundTo2(Number(giverAvailableTtsCredits || 0) - effectiveDelta);
      const exhausted = isAdd && nextDistributable <= 0;
      setGiverAvailableTtsCredits(exhausted ? 0 : nextDistributable);
    }

    window.dispatchEvent(new CustomEvent('tts-credits-updated'));

    if (!isSuperAdmin) refreshGiverTtsCredits();

    setConfirmTtsCredits(false);
    setConfirmReduceTtsCredits(false);
    setTtsCreditsSubmitting(false);
    setRemoveTtsCreditsValue(0);
    setAddTtsCreditsValue('');
    setAddTtsCreditsMultiplier(1);
    setShowAddTtsCreditModel(false);
    setShowRemoveTtsCreditModel(false);
    toast.success('Company TTS Credits Updated Successfully');
  }

  function onUserTtsCreditsUpdatedFailed(error) {
    setConfirmTtsCredits(false);
    setConfirmReduceTtsCredits(false);
    setTtsCreditsSubmitting(false);
    setRemoveTtsCreditsValue(0);
    setAddTtsCreditsValue('');
    setAddTtsCreditsMultiplier(1);
    setShowAddTtsCreditModel(false);
    setShowRemoveTtsCreditModel(false);
    toast.error(friendlyApiError(error) || 'Failed to update TTS credits');
  }

  useEffect(() => {
    if (confirmAiAgentCredits) {
      if (!addAiAgentCreditsValue || Number(addAiAgentCreditsValue) === 0) {
        toast.error('Please add some AI agent voice credit value to increase');
        setConfirmAiAgentCredits(false);
      } else {
        const effectiveAmount =
          Number(addAiAgentCreditsValue) * (Number(addAiAgentCreditsMultiplier) || 1);
        if (!isSuperAdmin && effectiveAmount > giverAvailableAiAgentCredits) {
          toast.error(
            `Insufficient distributable AI agent voice credits. You have ${giverAvailableAiAgentCredits} distributable but need ${effectiveAmount} (${addAiAgentCreditsValue} × ${addAiAgentCreditsMultiplier}x). Please ask your parent to add more credits.`,
          );
          setConfirmAiAgentCredits(false);
          return;
        }
        let apiUrl = API_AI_AGENT_CREDITS.replace(':id', addAiAgentCreditsId);
        setAiAgentCreditsSubmitting(true);
        postApiCall(apiUrl, {
          id: addAiAgentCreditsId,
          updatedCredit: addAiAgentCreditsValue,
          multiplier: addAiAgentCreditsMultiplier,
          action: AI_AGENT_CREDITS_ACTION.ADDITION,
          fromUser: loggedInUserId,
        })
          .then(onUserAiAgentCreditsUpdatedSuccess)
          .catch(onUserAiAgentCreditsUpdatedFailed);
      }
    }
  }, [confirmAiAgentCredits, addAiAgentCreditsValue, addAiAgentCreditsMultiplier, isSuperAdmin, giverAvailableAiAgentCredits]);

  useEffect(() => {
    if (confirmReduceAiAgentCredits) {
      if (removeAiAgentCreditsValue == 0) {
        toast.error('Please add some AI agent voice credit value to reduce');
      } else {
        let apiUrl = API_AI_AGENT_CREDITS.replace(':id', removeAiAgentCreditsId);
        setAiAgentCreditsSubmitting(true);
        postApiCall(apiUrl, {
          id: removeAiAgentCreditsId,
          updatedCredit: removeAiAgentCreditsValue,
          action: AI_AGENT_CREDITS_ACTION.DEDUCTION,
          fromUser: loggedInUserId,
        })
          .then(onUserAiAgentCreditsUpdatedSuccess)
          .catch(onUserAiAgentCreditsUpdatedFailed);
      }
    }
  }, [confirmReduceAiAgentCredits, removeAiAgentCreditsValue]);

  function onUserAiAgentCreditsUpdatedSuccess(resp) {
    const updatedCompanyId = addAiAgentCreditsId || removeAiAgentCreditsId;
    const isAdd = confirmAiAgentCredits;
    const fromCompanyId =
      loggedInUser?.assignedCompany?.id ?? loggedInUser?.assigned_company;
    const targetSnapshot = companylist.find((c) => c.id === updatedCompanyId);
    const locked = lockedCreditMultiplier(
      targetSnapshot,
      'ai_agent_credits_multiplier',
      'stored_ai_agent_credits_multiplier',
      'ai_agent_credits',
      'ai_agent_credits_distributable',
    );
    const appliedMult =
      locked ??
      (isAdd
        ? Number(addAiAgentCreditsMultiplier) || 1
        : positiveCreditMultiplier(targetSnapshot?.stored_ai_agent_credits_multiplier) ?? 1);
    const amount = isAdd ? Number(addAiAgentCreditsValue) : Number(removeAiAgentCreditsValue);
    const baseDelta = isAdd ? amount : -amount;
    const effectiveDelta = baseDelta * appliedMult;

    setCompanylist((prevList) =>
      prevList.map((company) => {
        if (company.id === updatedCompanyId) {
          const currentAvailable = Number(company.ai_agent_credits || 0);
          const currentDistributable = Number(
            company.ai_agent_credits_distributable ?? company.ai_agent_credits ?? 0,
          );
          const zeroPassedDownPool =
            !isAdd && isSuperAdmin && currentAvailable * appliedMult > currentDistributable;
          const nextDistributable = zeroPassedDownPool
            ? 0
            : roundTo2(
                !isAdd && isSuperAdmin
                  ? Math.max(0, currentDistributable + effectiveDelta)
                  : currentDistributable + effectiveDelta,
              );
          return {
            ...company,
            ai_agent_credits: roundTo2(currentAvailable + baseDelta),
            ai_agent_credits_distributable: nextDistributable,
            stored_ai_agent_credits_multiplier: appliedMult,
            ai_agent_credits_multiplier:
              zeroPassedDownPool || (!isAdd && nextDistributable <= 0) ? 0 : appliedMult,
          };
        }
        if (!isSuperAdmin && fromCompanyId && company.id === fromCompanyId) {
          const nextDistributable = roundTo2(
            Number(company.ai_agent_credits_distributable ?? company.ai_agent_credits ?? 0) -
              effectiveDelta,
          );
          const exhausted = isAdd && nextDistributable <= 0;
          const restored =
            !isAdd &&
            nextDistributable > 0 &&
            Number(company.ai_agent_credits_multiplier ?? 1) === 0;
          return {
            ...company,
            ai_agent_credits_distributable: exhausted ? 0 : nextDistributable,
            ai_agent_credits_multiplier: exhausted
              ? 0
              : restored
              ? Number(
                  company.stored_ai_agent_credits_multiplier ??
                    company.ai_agent_credits_multiplier ??
                    1,
                )
              : company.ai_agent_credits_multiplier,
          };
        }
        return company;
      }),
    );

    if (!isSuperAdmin && fromCompanyId && loggedInUser?.assignedCompany?.id === fromCompanyId) {
      const nextDistributable = roundTo2(
        Number(giverAvailableAiAgentCredits || 0) - effectiveDelta,
      );
      const exhausted = isAdd && nextDistributable <= 0;
      setGiverAvailableAiAgentCredits(exhausted ? 0 : nextDistributable);
    }

    window.dispatchEvent(new CustomEvent('ai-agent-credits-updated'));

    if (!isSuperAdmin) refreshGiverAiAgentCredits();

    setConfirmAiAgentCredits(false);
    setConfirmReduceAiAgentCredits(false);
    setAiAgentCreditsSubmitting(false);
    setRemoveAiAgentCreditsValue(0);
    setAddAiAgentCreditsValue('');
    setAddAiAgentCreditsMultiplier(1);
    setShowAddAiAgentCreditModel(false);
    setShowRemoveAiAgentCreditModel(false);
    toast.success('Company AI Agent Voice Credits Updated Successfully');
  }

  function onUserAiAgentCreditsUpdatedFailed(error) {
    setConfirmAiAgentCredits(false);
    setConfirmReduceAiAgentCredits(false);
    setAiAgentCreditsSubmitting(false);
    setRemoveAiAgentCreditsValue(0);
    setAddAiAgentCreditsValue('');
    setAddAiAgentCreditsMultiplier(1);
    setShowAddAiAgentCreditModel(false);
    setShowRemoveAiAgentCreditModel(false);
    toast.error(friendlyApiError(error) || 'Failed to update AI agent voice credits');
  }

  const displayedCompanyList = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return companylist;
    return companylist.filter((c) => (c?.name || '').toLowerCase().includes(q));
  }, [companylist, searchValue]);

  const isCallCentreScopedRole = [
    USER_ROLES.CALL_CENTRE_ADMIN,
    USER_ROLES.CALL_CENTRE_TEAM_LEAD,
    USER_ROLES.CALL_CENTRE_AGENT,
    USER_ROLES.CALL_CENTRE,
  ].includes(loggedInUser?.role);

  const canRead = hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.COMPANY, 'read');
  const canAdd =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.COMPANY, 'add');
  const canEdit =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.COMPANY, 'edit');
  const canDelete =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.COMPANY, 'delete');

  const isDemoRowExpired = (rowData) => {
    const exp = rowData?.defaultUser?.expiry_date;
    if (!exp) return false;
    const e = new Date(exp);
    e.setHours(0, 0, 0, 0);
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return !Number.isNaN(e.getTime()) && e.getTime() < t.getTime();
  };

  const isCompanyRowExpired = (rowData) => {
    const raw = rowData?.expiry_date || rowData?.defaultUser?.expiry_date;
    if (!raw) return false;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return false;
    const e = new Date(d);
    e.setHours(0, 0, 0, 0);
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return e.getTime() < t.getTime();
  };

  const fetchCompanyLicence = async (companyId) => {
    if (!companyId) return;
    try {
      setLicenceModalLoading(true);
      const url = API_COMPANY_LICENCE.replace(':id', companyId);
      const resp = await getApiCall(url, {});
      setLicenceModalData(resp || null);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to load licence');
      setLicenceModalData(null);
    } finally {
      setLicenceModalLoading(false);
    }
  };

  const openCompanyLicence = (rowData) => {
    setLicenceModalId(rowData.id);
    setLicenceModalTitle(`Licence — ${rowData.name || ''}`);
    setLicenceModalData(null);
    setShowLicenceModal(true);
    fetchCompanyLicence(rowData.id);
  };

  const openCompanyDetailsModal = (rowData) => {
    setSelectedCompanyDetails(rowData);
    setShowCompanyDetailsModal(true);
  };

  const formatDetailDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const RenderActionComponent = (companyRow) => {
    const id = companyRow.id;
    const isDemoRow = companyRow.is_demo_company === true;
    const companyEditRoute = ROUTES.COMPANIES.EDIT.replace(':id', id);
    const isParentOfCompany = isCompanyParent(companyRow);

    const showEdit = isParentOfCompany && canEdit;
    const showAllocate = isParentOfCompany && canEdit;
    const showCreditActions = isParentOfCompany && canEdit;
    const showConvert = isDemoRow && isSuperAdmin;
    const showDelete = canDelete && (isParentOfCompany || isSuperAdmin);

    if (!showEdit && !showAllocate && !showCreditActions && !showConvert && !showDelete) {
      return <span className="text-muted">—</span>;
    }

    return (
      <CompanyRowActions
        company={companyRow}
        showEdit={showEdit}
        showAllocate={showAllocate}
        showCreditActions={showCreditActions}
        showConvert={showConvert}
        showDelete={showDelete}
        isDemoRow={isDemoRow}
        onEdit={() => navigate(`/${companyEditRoute}`)}
        onAllocate={() => handleAllocateClick(id, companyRow.category)}
        onAddVoiceCredits={() => handleAddCredits(id)}
        onRemoveVoiceCredits={() => handleRemoveCredits(id)}
        onAddTtsCredits={() => handleAddTtsCredits(id)}
        onRemoveTtsCredits={() => handleRemoveTtsCredits(id)}
        onAddAiAgentCredits={() => handleAddAiAgentCredits(id)}
        onRemoveAiAgentCredits={() => handleRemoveAiAgentCredits(id)}
        onConvert={() => openConvertModal(id)}
        onDelete={() => openDeleteModal(id)}
      />
    );
  };


  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      { accessorKey: 'key', header: '#', enableGlobalFilter: false },
      {
        id: 'company',
        header: 'Company',
        accessorFn: (row) => `${row.name || ''} ${row.company_code || ''}`,
        // The company-name cell was wrapping vertically ("apii/mpr/ove/nd")
        // because the column was defaulting to a very narrow width. Give
        // it enough room for a two-line name at most.
        size: 220,
        minSize: 180,
        cell: ({ row }) => {
          const rowData = row.original;
          const code = Number(rowData.company_code)
            ? String(rowData.company_code).padStart(4, '0')
            : null;
          return (
            <div className="company-list-cell company-list-cell--company">
              <span className="company-list-avatar">{getCompanyInitials(rowData.name)}</span>
              <div className="company-list-company-text">
                <span className="company-list-company-name">{rowData.name || '-'}</span>
                {code && <span className="company-list-company-code">Code: {code}</span>}
              </div>
            </div>
          );
        },
      },
      {
        id: 'details',
        header: 'Details',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            className="company-list-quick-btn company-list-quick-btn--strategy"
            title="View company details"
            onClick={() => openCompanyDetailsModal(row.original)}
          >
            <Eye size={16} />
          </button>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => {
          const { label, variant } = getCompanyTypeInfo(row.original);
          return (
            <span className={`company-list-type-badge company-list-type-badge--${variant}`}>
              {label}
            </span>
          );
        },
      },
      {
        id: 'billingPlan',
        header: 'Billing Plan',
        accessorFn: (row) => billingPlanLabel(row),
        cell: ({ row }) => billingPlanLabel(row.original),
      },
      // {
      //   id: 'assignedPlan',
      //   header: 'Assigned Plan Name',
      //   size: 180,
      //   minSize: 140,
      //   cell: ({ row }) => {
      //     const planName =
      //       row.original.assignedTtsPlan?.plan_name ||
      //       row.original.tts_plan?.plan_name;
      //     if (!planName) {
      //       return <span className="text-muted">—</span>;
      //     }
      //     return (
      //       <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-foreground">
      //         <FileText size={14} className="shrink-0 text-muted-foreground" />
      //         {planName}
      //       </span>
      //     );
      //   },
      // },
      {
        id: 'demo',
        header: 'Demo',
        cell: ({ row }) => (
          <span
            className={`company-list-type-badge company-list-type-badge--${
              row.original.is_demo_company ? 'reseller' : 'neutral'
            }`}
          >
            {row.original.is_demo_company ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        id: 'demoConvertedAt',
        header: 'Demo Converted At',
        cell: ({ row }) => {
          const raw = row.original?.demo_converted_at;
          if (!raw) return <span className="text-muted">—</span>;
          const d = new Date(raw);
          if (Number.isNaN(d.getTime())) return <span className="text-muted">—</span>;
          return (
            <span
              title={`Converted from demo on ${d.toLocaleString('en-IN')}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: '#16a34a',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.78rem',
                padding: '3px 9px',
                borderRadius: 12,
                whiteSpace: 'nowrap',
              }}
            >
              {d.toLocaleDateString('en-IN')}
            </span>
          );
        },
      },
    ];

    if (activeTab === 'demo' || activeTab === 'demo_deleted') {
      cols.push({
        id: 'demoRecharges',
        header: 'Recharges Done',
        cell: ({ row }) => {
          const rowData = row.original;
          if (rowData.is_demo_company !== true) return <span>—</span>;
          const used = Number(rowData.demo_recharge_count || 0);
          const max = Number(demoMaxRecharges || 0);
          return (
            <span
              className={`company-list-type-badge company-list-type-badge--${
                max > 0 && used >= max ? 'danger' : 'neutral'
              }`}
              title="Number of times this demo company has been recharged"
            >
              {max > 0 ? `${used} / ${max}` : used}
            </span>
          );
        },
      });
    }

    if (activeTab === 'demo_deleted') {
      cols.push({
        id: 'deletedAt',
        header: 'Deleted At',
        cell: ({ row }) => {
          const raw = row.original?.deleted_at || row.original?.updated_at;
          if (!raw) return <span className="text-muted">—</span>;
          const d = new Date(raw);
          if (Number.isNaN(d.getTime())) return <span className="text-muted">—</span>;
          return <span title={d.toLocaleString('en-IN')}>{d.toLocaleString('en-IN')}</span>;
        },
      });
    }

    cols.push({
      id: 'expiry',
      header: activeTab === 'demo_deleted' ? 'Status' : 'Expiry / Status',
      cell: ({ row }) => {
        const rowData = row.original;
        if (rowData?.is_deleted === true || activeTab === 'demo_deleted') {
          const raw = rowData?.deleted_at || rowData?.updated_at;
          const d = raw ? new Date(raw) : null;
          const valid = d && !Number.isNaN(d.getTime());
          return (
            <div className="d-flex flex-column">
              <span>{valid ? d.toLocaleDateString('en-IN') : '—'}</span>
              <span
                className="company-list-type-badge company-list-type-badge--neutral"
                style={{ width: 'fit-content', marginTop: 2 }}
              >
                Deleted
              </span>
            </div>
          );
        }
        const raw = rowData?.expiry_date || rowData?.defaultUser?.expiry_date;
        if (!raw) return <span>—</span>;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return <span>—</span>;
        const expired = isCompanyRowExpired(rowData);
        return (
          <div className="d-flex flex-column">
            <span>{d.toLocaleDateString('en-IN')}</span>
            <span
              className={`company-list-type-badge company-list-type-badge--${
                expired ? 'danger' : 'reseller'
              }`}
              style={{ width: 'fit-content', marginTop: 2 }}
            >
              {expired ? 'Expired' : 'Active'}
            </span>
          </div>
        );
      },
    });

    cols.push({
      id: 'demoSummary',
      header: 'Demo Summary',
      cell: ({ row }) => {
        const rowData = row.original;
        const canView = rowData?.is_demo_company === true || !!rowData?.demo_converted_at;
        if (!canView) return <span>—</span>;
        return (
          <button
            type="button"
            title="View demo summary"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/15 focus:outline-none focus:ring-2 focus:ring-primary/30"
            onClick={() => {
              setSummaryCompany({ id: rowData.id, name: rowData.name });
              setShowSummaryModal(true);
            }}
          >
            <FileText className="h-3.5 w-3.5" />
            Summary
          </button>
        );
      },
    });

    if (!isOwnCompany) {
      cols.push({
        id: 'createdBy',
        header: 'Created By',
        accessorFn: (row) => row.company_creator?.username || '',
        cell: ({ row }) => row.original.company_creator?.username || '-',
      });
      cols.push({
        id: 'credits',
        header: 'Credits',
        cell: ({ row }) => (
          <div className="company-list-cell company-list-cell--credits">
            <span className="company-list-credit-row">
              <span className="company-list-credit-badge company-list-credit-badge--vc">VC</span>
              <span className="company-list-credit-value">
                {formatCreditAmount(row.original.credits_available)}
              </span>
            </span>
            <span className="company-list-credit-row">
              <span className="company-list-credit-badge company-list-credit-badge--tts">TTS</span>
              <span className="company-list-credit-value">
                {formatCreditAmount(row.original.tts_credits)}
              </span>
            </span>
            <span className="company-list-credit-row">
              <span className="company-list-credit-badge company-list-credit-badge--ai">AI</span>
              <span className="company-list-credit-value">
                {formatCreditAmount(row.original.ai_agent_credits)}
              </span>
            </span>
          </div>
        ),
      });
      cols.push({
        id: 'quickAccess',
        header: 'Quick Access',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="company-list-quick-access">
            <QuickAccessBtn
              title="View call strategies, dialer groups, locations and country codes"
              accent="strategy"
              onClick={() => setQuickAccessCompany(row.original)}
            >
              <Eye size={16} />
            </QuickAccessBtn>
          </div>
        ),
      });
      // Four row-action columns share the same visual language as the
      // Quick Access column above — 32×32 rounded icon button with a
      // semantic accent. Consolidates a long strip of text buttons into
      // a scannable icon row that still reveals its action via `title`.
      cols.push({
        id: 'hierarchy',
        header: 'Hierarchy',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            className="company-list-quick-btn company-list-quick-btn--history"
            title="View company hierarchy"
            onClick={() => {
              setHierarchyCompany(row.original);
              setShowHierarchyModal(true);
            }}
          >
            <Network size={16} />
          </button>
        ),
      });
      cols.push({
        id: 'licence',
        header: 'Licence',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-col items-start gap-1">
            <button
              type="button"
              className="company-list-quick-btn company-list-quick-btn--history"
              title="View licence history"
              onClick={() => openCompanyLicenceHistory(row.original)}
            >
              <History size={16} />
            </button>
            <button
              type="button"
              className="company-list-quick-btn company-list-quick-btn--strategy"
              title="View licence"
              onClick={() => openCompanyLicence(row.original)}
            >
              <Award size={16} />
            </button>
          </div>
        ),
      });
      cols.push({
        id: 'voiceCreditsHistory',
        header: 'Credits History',
        size: 120,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="company-list-quick-btn company-list-quick-btn--locations"
              title="View voice-credits history"
              onClick={() => openCompanyVoiceCreditsHistory(row.original)}
            >
              <Wallet size={16} />
            </button>
            <button
              type="button"
              className="company-list-quick-btn company-list-quick-btn--strategy"
              title="View TTS-credits history"
              onClick={() => openCompanyTtsCreditsHistory(row.original)}
            >
              <Volume2 size={16} />
            </button>
            <button
              type="button"
              className="company-list-quick-btn company-list-quick-btn--aiagent"
              title="View AI agent voice-credits history"
              onClick={() => openCompanyAiAgentCreditsHistory(row.original)}
            >
              <Bot size={16} />
            </button>
          </div>
        ),
      });
      cols.push({
        id: 'defaultUser',
        header: 'Default User',
        accessorFn: (row) => row.defaultUser?.username || '',
        enableSorting: false,
        // enableHiding: false — this column hosts the "switch to default
        // user" icon; hiding it removes the only entry point to switch
        // into a tenant. See UserListPage 'action' column for the same
        // guard rationale (persisted visibility in localStorage).
        enableHiding: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const defaultUser = rowData.defaultUser;
          if (isOwnCompany) {
            return <span className="company-list-default-user">{defaultUser?.username || '-'}</span>;
          }
          // `isDemoRowExpired` is a mis-named helper: it just checks whether
          // the default user's expiry_date is in the past, regardless of
          // whether the row is a demo company. Applying it to non-demo rows
          // silently dims the switch icon for real tenant companies whose
          // default user has any historical expiry set. Scope the check to
          // actual demo companies only — matches the column name intent
          // and v1's behaviour of lighting up every non-demo row.
          const demoExpired =
            rowData?.is_demo_company === true && isDemoRowExpired(rowData);
          const isDeletedDemo = rowData?.is_deleted === true || activeTab === 'demo_deleted';
          // v2 API shapes the default-user payload differently from v1 —
          // status can arrive as any of `1`, `'1'`, `'Active'`, or even
          // just plain missing when the user is present-but-untouched.
          // v1's `Number(status) === 1` guard lit up every row because
          // v1 always returned a numeric 1. To match v1's UX in v2, gate
          // only on the real blockers: no default user at all, deleted
          // demo, or expired demo.
          const canSwitchDefaultUser =
            Boolean(defaultUser?.id) && !demoExpired && !isDeletedDemo;
          return (
            <div className="company-list-default-user-cell">
              {canSwitchDefaultUser && (
                <span
                  className="company-list-default-user-switch"
                  title={
                    isSwitchingAccount ? 'Switching...' : `Switch to ${defaultUser.username}`
                  }
                  onClick={() => {
                    if (!isSwitchingAccount) switchToDefaultUser(rowData);
                  }}
                  style={{
                    opacity: isSwitchingAccount ? 0.6 : 1,
                    cursor: isSwitchingAccount ? 'not-allowed' : 'pointer',
                  }}
                >
                  <img alt="Switch to default user" src={switchUserIcon} width={26} />
                </span>
              )}
              {!canSwitchDefaultUser && isDeletedDemo && (
                <span
                  className="company-list-default-user-switch"
                  title="Demo company deleted — switch disabled"
                  style={{ opacity: 0.4, cursor: 'not-allowed' }}
                >
                  <img alt="Switch disabled" src={switchUserIcon} width={26} />
                </span>
              )}
              {!canSwitchDefaultUser && !isDeletedDemo && (
                <span
                  className="company-list-default-user-switch"
                  style={{ opacity: 0.4, cursor: 'not-allowed' }}
                >
                  <img alt="Switch disabled" src={switchUserIcon} width={26} />
                </span>
              )}
              <span className="company-list-default-user">{defaultUser?.username || '-'}</span>
            </div>
          );
        },
      });
    }

    if (activeTab !== 'demo_deleted') {
      cols.push({
        id: 'actionsOrLicence',
        header: isOwnCompany ? 'Current Licence' : 'Actions',
        enableSorting: false,
        size: 260,
        cell: ({ row }) => {
          const rowData = row.original;
          if (isOwnCompany) {
            const lic = rowData.available_licence;
            if (!lic) return <span className="text-muted-foreground">—</span>;
            const items = [
              { label: 'Channels', short: 'CH', value: lic.channel ?? 0, title: 'Channels' },
              { label: 'AI Agents', short: 'AI', value: lic.ai_agent ?? 0, title: 'AI Agents' },
              {
                label: 'AI Live Channels',
                short: 'AIC',
                value: lic.ai_agent_live_channel ?? 0,
                title: 'AI Agent Live Channels',
              },
              { label: 'Live Agents', short: 'LA', value: lic.live_agent ?? 0, title: 'Live Agents' },
              { label: 'Call Centres', short: 'CC', value: lic.role_ccadmin ?? 0, title: 'Call Centres' },
              { label: 'Total Agents', short: 'TA', value: lic.role_ccagent ?? 0, title: 'Total Agents' },
              { label: 'Team Leads', short: 'TL', value: lic.role_teamlead ?? 0, title: 'Total Team Leads' },
              { label: 'CPS', short: 'CPS', value: lic.cps ?? 0, title: 'Calls Per Second' },
              { label: 'Mobile', short: 'MO', value: lic.mobile ?? 0, title: 'Mobile' },
            ];
            return (
              <div className="grid max-w-[240px] grid-cols-4 gap-1">
                {items.map((it) => (
                  <div
                    key={it.short}
                    title={`${it.title}: ${it.value}`}
                    className="flex flex-col items-center justify-center rounded-md border border-border/60 bg-muted/40 px-1 py-1 leading-none"
                  >
                    <span className="font-mono text-[13px] font-bold text-foreground">
                      {it.value}
                    </span>
                    <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {it.short}
                    </span>
                  </div>
                ))}
              </div>
            );
          }
          return RenderActionComponent(rowData);
        },
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOwnCompany,
    activeTab,
    demoMaxRecharges,
    isSwitchingAccount,
    canDelete,
    canEdit,
    isSuperAdmin,
    loggedInUserACL,
    currentUserId,
  ]);

  if (!canRead) {
    return (
      <div
        className="text-center p-4"
        style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) {
    return <Loader />;
  }

  const addVoiceCompany = companylist.find((c) => c.id === addCreditsId);
  const addTtsCompany = companylist.find((c) => c.id === addTtsCreditsId);
  const addAiCompany = companylist.find((c) => c.id === addAiAgentCreditsId);

  return (
    <FillPage>
      <PageHeader
        compact
        className="!mb-0 shrink-0"
        title="Companies"
        description="Tenant companies you manage — their credits, hierarchy, and demo state."
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                isRefreshRef.current = true;
                setReloadKey((k) => k + 1);
              }}
              disabled={refreshing || loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={'h-4 w-4 ' + (refreshing ? 'animate-spin' : '')} />
            </Button>
            {companylist.length > 0 && !isOwnCompany && (
              <Button variant="outline" onClick={handleExport}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && !isOwnCompany && (
                <Button onClick={handleCreateButtonClick}>
                  <Plus className="h-4 w-4" />
                  Create Company
                </Button>
              )}
          </>
        }
      />
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
        {!isOwnCompany && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4 shrink-0">
            <TabsList
              className="h-auto gap-1 rounded-full border border-border/60 bg-muted/40 p-1 shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)]"
            >
              <TabsTrigger
                value="mine"
                className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
              >
                <Building2 className="h-3.5 w-3.5" />
                My Companies
              </TabsTrigger>
              <TabsTrigger
                value="nested"
                className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
              >
                <Network className="h-3.5 w-3.5" />
                All / Nested
              </TabsTrigger>
              <TabsTrigger
                value="demo"
                className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Demo
              </TabsTrigger>
              {isSuperAdmin && (
                <TabsTrigger
                  value="demo_deleted"
                  className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-destructive/15 data-[state=active]:!text-destructive data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Demo Deleted
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
        )}

        {/* Custom name-only search removed — DataTable's own toolbar
            search covers the same use case. `searchValue` state is
            kept (and stays ''), so `displayedCompanyList` becomes a
            no-op pass-through of `companylist`. */}
        <div className="hidden">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search company by name..."
              maxLength={100}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <DataTable
            tableId="companies.list:v2"
            columns={columns}
            data={displayedCompanyList}
            isLoading={loading}
            emptyTitle="No companies"
            emptyDescription="No companies match the current filters."
            searchPlaceholder="Search by company, default user, created by…"
            pageSize={8}
            disableExport
          />
        </div>
      </Card>

      <RenderCompanyHierarchyModal
        show={showHierarchyModal}
        setShow={setShowHierarchyModal}
        company={hierarchyCompany}
      />

      <RenderAddCreditModal
        show={showAddCreditModel}
        setShow={setShowAddCreditModel}
        setConfirmUpdateCredit={setConfirmCredits}
        submitting={creditsSubmitting}
        creditsValue={addCreditsValue}
        setCreditsValue={setAddCreditsValue}
        multiplier={addCreditsMultiplier}
        setMultiplier={setAddCreditsMultiplier}
        showMultiplier={
          lockedCreditMultiplier(
            companylist.find((c) => c.id === addCreditsId),
            'voice_credits_multiplier',
            'stored_voice_credits_multiplier',
            'credits_available',
            'credits_distributable',
          ) == null &&
          companylist.find((c) => c.id === addCreditsId)?.is_own_company !== true
        }
        multiplierLocked={
          lockedCreditMultiplier(
            companylist.find((c) => c.id === addCreditsId),
            'voice_credits_multiplier',
            'stored_voice_credits_multiplier',
            'credits_available',
            'credits_distributable',
          ) != null
        }
        isUnlimitedCredits={isSuperAdmin}
        hideCurrentBalance={isSuperAdmin}
        balanceLabel="Your Balance:"
        balanceDisplay={isSuperAdmin ? null : giverDisplayCredits}
        availableCredits={isSuperAdmin ? 0 : giverAvailableCredits}
        showEffectiveCredits={!isSuperAdmin}
        subjectName={addVoiceCompany?.name || 'Selected company'}
        subjectBalanceLabel="Available voice credits"
        subjectBalance={Number(addVoiceCompany?.credits_available || 0)}
        isDemoTarget={
          companylist.find((c) => c.id === addCreditsId)?.is_demo_company === true
        }
        demoBalance={Number(
          companylist.find((c) => c.id === addCreditsId)?.credits_available || 0,
        )}
        demoMaxAmount={demoCreditLimit}
        demoRechargeUsed={Number(
          companylist.find((c) => c.id === addCreditsId)?.demo_recharge_count || 0,
        )}
        demoRechargeMax={demoMaxRecharges}
        demoExpired={isCompanyRowExpired(companylist.find((c) => c.id === addCreditsId))}
      />
      <RenderRemoveCreditModal
        show={showRemoveCreditModel}
        setShow={setShowRemoveCreditModel}
        setConfirmReduceUpdateCredit={setConfirmReduceCredits}
        submitting={creditsSubmitting}
        removeCreditsValue={removeCreditsValue}
        setRemoveCreditsValue={setRemoveCreditsValue}
        availableCredits={
          removeCreditsId
            ? isSuperAdmin
              ? superAdminRemovableCredits(
                  companylist.find((c) => c.id === removeCreditsId),
                  'credits_available',
                )
              : maxRemovableCredits(
                  companylist.find((c) => c.id === removeCreditsId),
                  'credits_available',
                  'credits_distributable',
                  'voice_credits_multiplier',
                  'stored_voice_credits_multiplier',
                )
            : 0
        }
        availableCreditsLabel="Maximum Removable Credits:"
      />

      <RenderAddCreditModal
        show={showAddTtsCreditModel}
        setShow={setShowAddTtsCreditModel}
        setConfirmUpdateCredit={setConfirmTtsCredits}
        submitting={ttsCreditsSubmitting}
        creditsValue={addTtsCreditsValue}
        setCreditsValue={setAddTtsCreditsValue}
        multiplier={addTtsCreditsMultiplier}
        setMultiplier={setAddTtsCreditsMultiplier}
        title="Add TTS Credits"
        showMultiplier={
          lockedCreditMultiplier(
            companylist.find((c) => c.id === addTtsCreditsId),
            'tts_credits_multiplier',
            'stored_tts_credits_multiplier',
            'tts_credits',
            'tts_credits_distributable',
          ) == null &&
          companylist.find((c) => c.id === addTtsCreditsId)?.is_own_company !== true
        }
        multiplierLocked={
          lockedCreditMultiplier(
            companylist.find((c) => c.id === addTtsCreditsId),
            'tts_credits_multiplier',
            'stored_tts_credits_multiplier',
            'tts_credits',
            'tts_credits_distributable',
          ) != null
        }
        isUnlimitedCredits={isSuperAdmin}
        hideCurrentBalance={isSuperAdmin}
        balanceLabel={isSuperAdmin ? 'Your Balance:' : 'Your TTS Balance:'}
        balanceDisplay={isSuperAdmin ? null : giverDisplayTtsCredits}
        availableCredits={isSuperAdmin ? 0 : giverAvailableTtsCredits}
        showEffectiveCredits={!isSuperAdmin}
        subjectName={addTtsCompany?.name || 'Selected company'}
        subjectBalanceLabel="Available TTS credits"
        subjectBalance={Number(addTtsCompany?.tts_credits || 0)}
      />
      <RenderRemoveCreditModal
        show={showRemoveTtsCreditModel}
        setShow={setShowRemoveTtsCreditModel}
        setConfirmReduceUpdateCredit={setConfirmReduceTtsCredits}
        submitting={ttsCreditsSubmitting}
        removeCreditsValue={removeTtsCreditsValue}
        setRemoveCreditsValue={setRemoveTtsCreditsValue}
        title="Remove TTS Credits"
        availableCredits={
          removeTtsCreditsId
            ? isSuperAdmin
              ? superAdminRemovableCredits(
                  companylist.find((c) => c.id === removeTtsCreditsId),
                  'tts_credits',
                )
              : maxRemovableCredits(
                  companylist.find((c) => c.id === removeTtsCreditsId),
                  'tts_credits',
                  'tts_credits_distributable',
                  'tts_credits_multiplier',
                  'stored_tts_credits_multiplier',
                )
            : 0
        }
        availableCreditsLabel="Maximum Removable Credits:"
      />

      <RenderAddCreditModal
        show={showAddAiAgentCreditModel}
        setShow={setShowAddAiAgentCreditModel}
        setConfirmUpdateCredit={setConfirmAiAgentCredits}
        submitting={aiAgentCreditsSubmitting}
        creditsValue={addAiAgentCreditsValue}
        setCreditsValue={setAddAiAgentCreditsValue}
        multiplier={addAiAgentCreditsMultiplier}
        setMultiplier={setAddAiAgentCreditsMultiplier}
        title="Add AI Agent Voice Credits"
        showMultiplier={
          lockedCreditMultiplier(
            companylist.find((c) => c.id === addAiAgentCreditsId),
            'ai_agent_credits_multiplier',
            'stored_ai_agent_credits_multiplier',
            'ai_agent_credits',
            'ai_agent_credits_distributable',
          ) == null &&
          companylist.find((c) => c.id === addAiAgentCreditsId)?.is_own_company !== true
        }
        multiplierLocked={
          lockedCreditMultiplier(
            companylist.find((c) => c.id === addAiAgentCreditsId),
            'ai_agent_credits_multiplier',
            'stored_ai_agent_credits_multiplier',
            'ai_agent_credits',
            'ai_agent_credits_distributable',
          ) != null
        }
        isUnlimitedCredits={isSuperAdmin}
        hideCurrentBalance={isSuperAdmin}
        balanceLabel={isSuperAdmin ? 'Your Balance:' : 'Your AI Agent Voice Balance:'}
        balanceDisplay={isSuperAdmin ? null : giverDisplayAiAgentCredits}
        availableCredits={isSuperAdmin ? 0 : giverAvailableAiAgentCredits}
        showEffectiveCredits={!isSuperAdmin}
        subjectName={addAiCompany?.name || 'Selected company'}
        subjectBalanceLabel="Available AI agent voice credits"
        subjectBalance={Number(addAiCompany?.ai_agent_credits || 0)}
      />
      <RenderRemoveCreditModal
        show={showRemoveAiAgentCreditModel}
        setShow={setShowRemoveAiAgentCreditModel}
        setConfirmReduceUpdateCredit={setConfirmReduceAiAgentCredits}
        submitting={aiAgentCreditsSubmitting}
        removeCreditsValue={removeAiAgentCreditsValue}
        setRemoveCreditsValue={setRemoveAiAgentCreditsValue}
        title="Remove AI Agent Voice Credits"
        availableCredits={
          removeAiAgentCreditsId
            ? isSuperAdmin
              ? superAdminRemovableCredits(
                  companylist.find((c) => c.id === removeAiAgentCreditsId),
                  'ai_agent_credits',
                )
              : maxRemovableCredits(
                  companylist.find((c) => c.id === removeAiAgentCreditsId),
                  'ai_agent_credits',
                  'ai_agent_credits_distributable',
                  'ai_agent_credits_multiplier',
                  'stored_ai_agent_credits_multiplier',
                )
            : 0
        }
        availableCreditsLabel="Maximum Removable Credits:"
      />

      {/* Company Details Modal */}
      <Modal
        show={showCompanyDetailsModal}
        onHide={() => {
          setShowCompanyDetailsModal(false);
          setSelectedCompanyDetails(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Company Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCompanyDetails && (() => {
            const rows: Array<{ icon: any; label: string; value: any }> = [
              { icon: Phone, label: 'Phone Number', value: selectedCompanyDetails.phone || '-' },
              { icon: MapPin, label: 'Address', value: selectedCompanyDetails.address || '-' },
              { icon: Hash, label: 'Pincode', value: selectedCompanyDetails.pincode || '-' },
              // {
              //   icon: FileText,
              //   label: 'Assigned Plan Name',
              //   value:
              //     selectedCompanyDetails.assignedTtsPlan?.plan_name ||
              //     selectedCompanyDetails.tts_plan?.plan_name ||
              //     '-',
              // },
              { icon: User, label: 'Created By', value: selectedCompanyDetails.company_creator?.username || '-' },
            ];
            if (selectedCompanyDetails.updated_at) {
              rows.push({
                icon: CalendarClock,
                label: 'Last Updated',
                value: formatDetailDateTime(selectedCompanyDetails.updated_at),
              });
            }
            rows.push({
              icon: Calendar,
              label: 'Created On',
              value: formatDetailDateTime(selectedCompanyDetails.created_at),
            });
            if (selectedCompanyDetails.defaultUser?.username) {
              rows.push({
                icon: UserRound,
                label: 'Default User',
                value: selectedCompanyDetails.defaultUser.username,
              });
            }
            return (
              <div className="divide-y divide-border rounded-md border">
                {rows.map((r, i) => {
                  const Icon = r.icon;
                  return (
                    <div
                      key={i}
                      className="flex items-start justify-between gap-4 px-4 py-3"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Icon size={15} strokeWidth={2.25} className="flex-none" />
                        {r.label}
                      </span>
                      <span className="text-right text-sm text-foreground break-words">
                        {r.value}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Modal.Body>
      </Modal>

      <CompanyQuickAccessModal
        company={quickAccessCompany}
        onClose={() => setQuickAccessCompany(null)}
        fetchAssociations={fetchCompanyAssociations}
        onLoaded={mergeCompanyRow}
      />

      {/* Delete confirmation */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to deactivate this company? It will be hidden from the company
          list but not removed from the database. Licence values cannot be returned.
          <div className="text-muted mt-2" style={{ fontSize: '0.9rem' }}>
            All users under this company must be blocked before deactivation.
          </div>
          <div className="text-danger mt-2" style={{ fontSize: '0.9rem' }}>
            <b>Note:</b> All DID number(s) allocated to this company and its nested companies will
            be released back to the free pool. Their routing and voice plan allocation will be
            cleared and the numbers will become available for re-allocation.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="light" onClick={closeDeleteModal}>
            Cancel
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmDelete} disabled={buttonDisable}>
            Delete
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      {/* Convert to actual */}
      <Modal show={showConvertModal} onHide={closeConvertModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Convert to Actual Company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to convert this demo company into an actual company? This removes
          all demo restrictions, sets its voice credits to 0, and resets its agents to a normal
          login expiry. This action cannot be undone.
          <div
            style={{
              marginTop: '12px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #fca5a5',
              background: '#fef2f2',
              color: '#b91c1c',
              fontSize: '0.9rem',
            }}
          >
            <strong>Note:</strong> All agents created under this demo account will be{' '}
            <strong> deleted</strong> and removed from every call group, inbound route, campaign,
            lead and follow-up. You will need to create new agents after conversion.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="light" onClick={closeConvertModal}>
            Cancel
          </BootstrapButton>
          <BootstrapButton variant="success" onClick={confirmConvert} disabled={buttonDisable}>
            Convert
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <DemoCompanySummaryModal
        show={showSummaryModal}
        onHide={() => setShowSummaryModal(false)}
        companyId={summaryCompany.id}
        companyName={summaryCompany.name}
      />
      <LicenceDetailsModal
        show={showLicenceModal}
        onHide={() => setShowLicenceModal(false)}
        title={licenceModalTitle}
        licence={licenceModalData}
        loading={licenceModalLoading}
        onRefresh={() => fetchCompanyLicence(licenceModalId)}
      />

      {/* Licence History — modal shell verbatim, body placeholder until
         v1 licence_history/list.js ports as its own module */}
      <Modal
        show={showLicenceHistoryModal}
        onHide={() => {
          setShowLicenceHistoryModal(false);
          setSelectedLicenceCompanyId(null);
          setSelectedLicenceCompanyName(null);
        }}
        centered
        size="xl"
      >
        <Modal.Header>
          <Modal.Title className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
              <History className="h-4 w-4" />
            </span>
            <span className="flex flex-col">
              <span>Licence History</span>
              {selectedLicenceCompanyName && (
                <span className="text-xs font-normal text-muted-foreground">
                  {selectedLicenceCompanyName}
                </span>
              )}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLicenceCompanyId && (
            <LicenceHistoryList
              companyId={selectedLicenceCompanyId}
              displayName={selectedLicenceCompanyName}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Voice Credits History — same pattern */}
      <Modal
        show={showVoiceCreditsHistoryModal}
        onHide={() => {
          setShowVoiceCreditsHistoryModal(false);
          setSelectedVoiceCreditsCompanyId(null);
          setSelectedVoiceCreditsCompanyName(null);
        }}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Voice Credits History — {selectedVoiceCreditsCompanyName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVoiceCreditsCompanyId && (
            <CompanyVoiceCreditsHistory
              companyId={selectedVoiceCreditsCompanyId}
              displayName={selectedVoiceCreditsCompanyName}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* TTS Credits History — same pattern as voice */}
      <Modal
        show={showTtsCreditsHistoryModal}
        onHide={() => {
          setShowTtsCreditsHistoryModal(false);
          setSelectedTtsCreditsCompanyId(null);
          setSelectedTtsCreditsCompanyName(null);
        }}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>TTS Credits History — {selectedTtsCreditsCompanyName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTtsCreditsCompanyId && (
            <CompanyVoiceCreditsHistory
              companyId={selectedTtsCreditsCompanyId}
              displayName={selectedTtsCreditsCompanyName}
              logsApiUrl={API_TTS_CREDITS_LOGS}
              emptyMessage="No TTS credits history found for this company"
              exportFilePrefix="tts-credits-history"
              displayBalanceKey="tts_credits"
              distributableBalanceKey="tts_credits_distributable"
            />
          )}
        </Modal.Body>
      </Modal>

      {/* AI Agent Voice Credits History — same pattern as TTS */}
      <Modal
        show={showAiAgentCreditsHistoryModal}
        onHide={() => {
          setShowAiAgentCreditsHistoryModal(false);
          setSelectedAiAgentCreditsCompanyId(null);
          setSelectedAiAgentCreditsCompanyName(null);
        }}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            AI Agent Voice Credits History — {selectedAiAgentCreditsCompanyName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAiAgentCreditsCompanyId && (
            <CompanyVoiceCreditsHistory
              companyId={selectedAiAgentCreditsCompanyId}
              displayName={selectedAiAgentCreditsCompanyName}
              logsApiUrl={API_AI_AGENT_CREDITS_LOGS}
              emptyMessage="No AI agent voice credits history found for this company"
              exportFilePrefix="ai-agent-credits-history"
              displayBalanceKey="ai_agent_credits"
              distributableBalanceKey="ai_agent_credits_distributable"
            />
          )}
        </Modal.Body>
      </Modal>

    </FillPage>
  );
};

export default CompanyList;
